/**
 * CORTEX events – single source of truth for event listing, nav, news, timeline, and posters.
 * Used by: Cortex.js (event cards), nav.jsx (CORTEX dropdown), PromotionalHome.js (news, timeline, event poster).
 * Add or edit events here; register the page in config/eventIndex.js so the route is created.
 * Use getEventPosterPath('cortex', slug) from config/eventPaths.js for standard poster paths.
 */

import { getEventPosterPath, getEventImagePath, getEventDocumentPath } from './eventPaths';

export const CORTEX_EVENTS = [
  {
    slug: 'member-registration',
    path: '/cortex/member-registration',
    title: 'Membership Registration',
    shortDescription:
      'Register as a CORTEX member — sign up using the form on this page.',
    dateLabel: 'Open year-round',
    accentColor: 'amber',
    showInNav: true,
    navLabel: 'Membership Registration',
    showInNews: false,
    order: 0,
    /** Listing order is reverse-chronological by sortDate (newer dates first). Set this after Conference 2026’s sortDate so membership appears above it on /cortex and in the nav. */
    sortDate: '2126-04-21',
    cardLayout: 'simple',
  },
  {
    slug: 'essay-competition-2025',
    path: '/cortex/essay-competition-2025',
    title: 'CORTEX Essay Competition 2025',
    shortDescription: 'We all take common core courses and study general education; yet what exactly is transdisciplinarity? Join our essay competition and share your unique perspectives on how transdisciplinarity is involved in your areas of interest. Winners may have their work published in the Journal for Experiential Learning and Transdisciplinary Research (JELTR).',
    teaser: 'Two divisions: D1 (F4–F6) and D2 (Tertiary and above). Four themes: Human Identity & Experience, Global Citizenry, Artificial Intelligence, Sustainability.',
    details: [
      { label: 'Theme 1', value: 'Human Identity & Experience' },
      { label: 'Theme 2', value: 'Global Citizenry' },
      { label: 'Theme 3', value: 'Artificial Intelligence' },
      { label: 'Theme 4', value: 'Sustainability' },
    ],
    dateLabel: 'Submission deadline: 31 December 2025',
    accentColor: 'amber',
    showInNav: true,
    navLabel: 'Essay Competition 2025',
    showInNews: true,
    newsDateLabel: 'Dec 2025',
    newsCategory: 'Competition',
    newsBlurb: 'Share your unique perspectives on transdisciplinarity. Winners may have their work published in JELTR. Two divisions (F4–F6 and Tertiary+); four themes including AI and Sustainability.',
    order: 1,
    sortDate: '2025-12-31',
    cardLayout: 'rich',
    posterImage: getEventPosterPath('cortex', 'essay-competition-2025', 'png'),
    posterAlt: 'CORTEX Essay Competition 2025 Poster',
    posterSubtitle: 'Submission deadline: 31st December 2025',
    showOnPoster: true,
    posterOrder: 2,
    timelineEntries: [
      { date: 'Dec 31', label: 'Essay Competition 2025 Deadline' },
    ],
  },
  {
    slug: 'workshop-2026',
    path: '/cortex/workshop-2026',
    title: 'From Project to Conference 101: Abstract and Presentation Workshop',
    shortDescription: 'Have you ever looked at your final project and thought it could be something more? Most undergraduate research is better than it looks on a grade sheet! Join us for a workshop supported by the Leadership and Impact Award by Common Core. We\'ll help you take work you\'ve already done and shape it into a submission-ready abstract and presentation. No prior conference experience needed.',
    longDescription: 'Led by research postgraduates, this workshop will help you: distill your research into a compelling abstract; structure a clear and engaging presentation; leave with a submission-ready draft built from your own work. Open to students from all faculties. We recommend bringing a term paper or project you\'d like to develop. Seats are limited—register early to secure your spot.',
    dateLabel: '26 March 2026',
    dateLabelSecondary: '12:00 to 1:00 PM',
    accentColor: 'amber',
    showInNav: true,
    navLabel: 'Workshop: From Project to Conference 101',
    showInNews: true,
    newsDateLabel: 'Mar 2026',
    newsCategory: 'Workshop',
    newsBlurb: 'Turn your term paper or project into a conference-ready abstract and presentation. 26 Mar, Common Core Lounge. Pizza provided.',
    order: 2,
    sortDate: '2026-03-26',
    cardLayout: 'rich',
    posterImage: getEventImagePath('cortex', 'workshop-2026', 'CORTEX Workshop 1 Poster (IG).png'),
    posterAlt: 'From Project to Conference 101 Workshop Poster',
    posterSubtitle: '26 March 2026, 12:00–1:00 PM · Common Core Lounge',
    showOnPoster: true,
    posterOrder: 3,
    details: [
      { label: 'Date', value: '26 March 2026' },
      { label: 'Time', value: '12:00 to 1:00 PM' },
      { label: 'Venue', value: 'Common Core Lounge' },
      { label: 'Catering', value: 'Light meal provided (Pizza!)' },
    ],
    ctaLabel: 'View Instagram recap',
    ctaHref: 'https://www.instagram.com/p/DWt2s6-iaUL/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==',
    timelineEntries: [
      { date: 'Mar 26', label: 'From Project to Conference 101 Workshop' },
    ],
  },
  {
    slug: 'conference-2026-deadline-extension',
    path: '/cortex/conference-2026',
    title: 'Abstract Deadline Extended — CORTEX 2026',
    shortDescription:
      'Owing to popular demand, the abstract submission deadline for CORTEX Conference 2026 has been officially extended to 25 June 2026.',
    dateLabel: 'New deadline: 25 June 2026',
    accentColor: 'blue',
    showInNav: false,
    showInNews: true,
    showInListing: false,
    newsDateLabel: 'Jun 2026',
    newsCategory: 'Update',
    newsBlurb:
      'Good news! Owing to popular demand, the abstract submission deadline for CORTEX Conference 2026 has been officially extended to 25 June 2026. Don\'t miss this chance to present your work!',
    order: 5,
    sortDate: '2026-06-06',
    cardLayout: 'simple',
    ctaLabel: 'View on Instagram',
    ctaHref: 'https://www.instagram.com/p/DZNC9yapNsl/',
  },
  {
    slug: 'abstracts-2026',
    path: '/cortex#publications',
    title: 'CORTEX 2026 Book of Abstracts',
    shortDescription: 'Complete collection of all presentation abstracts from CORTEX 2026.',
    dateLabel: 'Published: 2 July 2026',
    accentColor: 'blue',
    showInNav: false,
    showInNews: true,
    newsDateLabel: 'Jul 2026',
    newsCategory: 'Publication',
    newsBlurb:
      'The CORTEX 2026 Book of Abstracts is now available on Zenodo! Explore abstracts from presenters across the globe who adopt a transdisciplinary approach in the sciences and humanities.',
    order: 3,
    sortDate: '2026-07-05',
    cardLayout: 'simple',
    showOnPoster: true,
    posterOrder: 0,
    posterImage: getEventDocumentPath('cortex', 'abstracts-2026', 'CORTEX BOA b5 v13.png'),
    posterAlt: 'CORTEX 2026 Book of Abstracts cover',
    posterSubtitle: 'Read the Book of Abstracts — See CORTEX',
    coverImage: getEventDocumentPath('cortex', 'abstracts-2026', 'CORTEX BOA b5 v13.png'),
    pdfPath: getEventDocumentPath('cortex', 'abstracts-2026', 'CORTEX 2026 Book of Abstracts.pdf'),
    zenodoUrl: 'https://doi.org/10.5281/zenodo.21129753',
  },
  {
    slug: 'conference-2026',
    path: '/cortex/conference-2026',
    title: 'CORTEX 2026',
    shortDescription: 'CORTEX is a student-led conference made for fellow undergraduates to present and publish their transdisciplinary ideas.',
    longDescription: 'We are not only looking for regular conference papers, but also research projects, experiential reports, and undergraduate theses!',
    dateLabel: 'Conference Date: 4th July 2026',
    dateLabelSecondary: 'Submission Deadline: 25th June 2026',
    accentColor: 'blue',
    showInNav: true,
    navLabel: 'Conference 2026',
    showInNews: true,
    newsDateLabel: 'Jul 2026',
    newsCategory: 'Conference',
    newsBlurb: 'Join us by 25 June at CORTEX 2026, a student-led conference at HKU showcasing transdisciplinary research, experiential learning, and innovative collaborations.',
    order: 3,
    sortDate: '2026-07-04',
    cardLayout: 'rich',
    posterImage: getEventImagePath('cortex', 'conference-2026', 'Poster Final.jpeg'),
    posterAlt: 'CORTEX Conference 2026 Poster',
    posterSubtitle: 'Submission: 25th June 2026 | Presentation: 4th July 2026',
    showOnPoster: true,
    posterOrder: 1,
    deadlineExtended: true,
    submissionDeadline: '25th June, 2026',
    previousSubmissionDeadline: '21st June, 2026',
    deadlineExtensionBlurb:
      'Owing to popular demand, the abstract submission deadline for CORTEX Conference 2026 has been officially extended.',
    deadlineExtensionInstagram: 'https://www.instagram.com/p/DZNC9yapNsl/',
    venue: 'Rm 4.35, XR Space, Arts Tech Lab, RRS Tower, Centennial Campus, HKU',
    details: [
      { label: 'Conference Date', value: '4th July, 2026' },
      { label: 'Submission Deadline', value: '25th June, 2026 (extended from 21 June)' },
      { label: 'Presentation Format', value: '15-minute presentation + 5-minute Q&A' },
      { label: 'Venue', value: 'Rm 4.35, XR Space, Arts Tech Lab, RRS Tower, Centennial Campus, HKU' },
    ],
    ctaLabel: 'Submit an Abstract',
    ctaHref: 'https://forms.gle/vinGcj181MLXHzSK7',
    /** Keynote speaker — key in config/advisors.js (ADVISORS); shown in Faculty Advisors grid with a rim highlight on /cortex/conference-2026. */
    keynoteAdvisorKey: 'ELAM',
    timelineEntries: [
      { date: 'Jun 25', label: 'CORTEX Submission Deadline' },
      { date: 'Jul 4', label: 'CORTEX Conference' },
    ],
  },
  {
    slug: 'keynote-speech-2026',
    path: '/cortex/keynote-speech-2026',
    title: 'Keynote Speech — CORTEX 2026',
    shortDescription:
      'Prof. J. Daniel Elam will deliver "Sociology Against Empire: Empiricism and the Anticolonial Imagination" — exploring how early social sciences gave anticolonial thinkers a wider intellectual toolbox.',
    dateLabel: '4 July 2026 · 10:00–10:45 AM',
    dateLabelSecondary: 'Rm 4.35, XR Space, Arts Tech Lab, RRS Tower, Centennial Campus, HKU',
    accentColor: 'blue',
    showInNav: true,
    navLabel: 'Keynote Speech',
    showInNews: true,
    newsDateLabel: 'Jul 2026',
    newsCategory: 'Keynote',
    newsBlurb:
      'Prof. J. Daniel Elam will deliver the CORTEX 2026 keynote speech "Sociology Against Empire: Empiricism and the Anticolonial Imagination" on 4th July at 10:00 AM in the Arts Tech Lab, HKU.',
    order: 4,
    sortDate: '2026-07-04',
    cardLayout: 'simple',
    posterImage: getEventImagePath('cortex', 'conference-2026', 'Keynote Final.jpeg'),
    posterAlt: 'CORTEX 2026 Keynote Speech — Prof. J. Daniel Elam',
    ctaLabel: 'View on Instagram',
    ctaHref: 'https://www.instagram.com/p/DZOj2Y_pZpn/',
    timelineEntries: [
      { date: 'Jul 4', label: 'CORTEX 2026 Keynote Speech' },
    ],
  },
];

/** Events to show in the Events section (all by default), sorted reverse chronologically (newest first) */
export const getCortexEventsForListing = () =>
  [...CORTEX_EVENTS]
    .filter((e) => e.showInListing !== false)
    .sort((a, b) => (b.sortDate || '0000-01-01').localeCompare(a.sortDate || '0000-01-01'));

/** Events to show in the nav dropdown */
export const getCortexEventsForNav = () =>
  getCortexEventsForListing().filter((e) => e.showInNav);

/** Events to show in the front-page News section (showInNews: true), sorted reverse chronologically */
export const getCortexEventsForNews = () =>
  [...CORTEX_EVENTS]
    .filter((e) => e.showInNews)
    .sort((a, b) => (b.sortDate || '0000-01-01').localeCompare(a.sortDate || '0000-01-01'));

/** Timeline entries from CORTEX events (for front-page Timeline). Each has date, label, path, sortDate (for grouping by year). */
export const getCortexTimelineEntries = () =>
  getCortexEventsForListing().flatMap((event) =>
    (event.timelineEntries || []).map((entry) => ({
      date: entry.date,
      label: entry.label,
      path: event.path,
      sortDate: event.sortDate,
      accentColor: 'blue',
    }))
  );

/** Get a single CORTEX event by slug (e.g. for event pages that read details from config). */
export const getCortexEventBySlug = (slug) =>
  CORTEX_EVENTS.find((e) => e.slug === slug) ?? null;

/** Footer badge on faculty cards: explains the blue rim for the conference keynote. */
export const CORTEX_2026_KEYNOTE_FOOTER_BADGE = 'CORTEX 2026 keynote speaker';

/** Advisor map key (config/advisors.js) for the CORTEX 2026 keynote, if set on conference-2026. */
export const getCortex2026KeynoteAdvisorKey = () =>
  getCortexEventBySlug('conference-2026')?.keynoteAdvisorKey ?? null;

/** First event with a poster image (for Event Dashboard on front page). */
export const getCortexFeaturedPosterEvent = () =>
  getCortexEventsForListing().find((e) => e.posterImage && e.showOnPoster) ||
  getCortexEventsForListing().find((e) => e.posterImage);

/** All events that can appear as dashboard posters (have posterImage + showOnPoster), sorted reverse chronologically */
export const getCortexEventsForPosters = () =>
  getCortexEventsForListing()
    .filter((e) => e.posterImage && e.showOnPoster)
    .sort((a, b) => (b.sortDate || '0000-01-01').localeCompare(a.sortDate || '0000-01-01'));
